<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import CampaignCenterAPI from '../api';
import {
  RECIPIENT_FILTERS,
  RECIPIENT_STATUS_COLOR,
  formatDateTime,
} from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  campaignId: { type: [Number, String], required: true },
  refreshKey: { type: Number, default: 0 },
});
const { t, locale } = useI18n();
const router = useRouter();

const rows = ref([]);
const total = ref(0);
const page = ref(1);
const perPage = ref(50);
const status = ref('all');
const search = ref('');
const isLoading = ref(false);

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await CampaignCenterAPI.recipients(props.campaignId, {
      page: page.value,
      status: status.value === 'all' ? undefined : status.value,
      q: search.value || undefined,
    });
    rows.value = data.payload;
    total.value = data.meta.total;
    perPage.value = data.meta.per_page;
  } finally {
    isLoading.value = false;
  }
};
const debouncedLoad = debounce(load, 400, false);

onMounted(load);
watch(() => props.refreshKey, load);
watch(status, () => {
  page.value = 1;
  load();
});
watch(search, () => {
  page.value = 1;
  debouncedLoad();
});
watch(page, load);

const statusLabel = row =>
  t(`WASAPI_CAMPAIGNS.DETAIL.RECIPIENT_STATUS.${row.status}`);
const errorLabel = row => {
  if (!row.error_code) return row.error || '';
  const known = t(`WASAPI_CAMPAIGNS.DETAIL.ERROR_CODES.${row.error_code}`);
  return known;
};
const openConversation = row => {
  if (!row.conversation_id) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: row.conversation_id },
  });
};
const fmt = v => formatDateTime(v, locale.value);
const hasFilter = computed(() => status.value !== 'all' || !!search.value);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center gap-2">
      <Button
        v-for="filter in RECIPIENT_FILTERS"
        :key="filter"
        :label="t(`WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.FILTERS.${filter}`)"
        :variant="status === filter ? 'solid' : 'faded'"
        color="slate"
        size="xs"
        @click="status = filter"
      />
      <span class="flex-1" />
      <Input
        v-model="search"
        type="search"
        :placeholder="
          t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.SEARCH_PLACEHOLDER')
        "
        class="w-64"
      />
    </div>
    <div v-if="isLoading && !rows.length" class="flex justify-center py-10">
      <Spinner />
    </div>
    <p
      v-else-if="!rows.length"
      class="py-10 text-sm text-center text-n-slate-11"
    >
      {{
        hasFilter
          ? t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.NO_RESULTS')
          : t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.EMPTY')
      }}
    </p>
    <div
      v-else
      class="overflow-x-auto rounded-xl outline outline-1 outline-n-weak"
    >
      <table class="w-full text-sm">
        <thead class="text-xs text-left uppercase text-n-slate-11 bg-n-alpha-1">
          <tr>
            <th class="px-3 py-2 font-medium">
              {{ t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.COLUMNS.CONTACT') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.COLUMNS.PHONE') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.COLUMNS.STATUS') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.COLUMNS.SENT_AT') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.COLUMNS.DELIVERED_AT') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.COLUMNS.READ_AT') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.COLUMNS.ERROR') }}
            </th>
            <th class="px-3 py-2" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-for="row in rows" :key="row.id" class="hover:bg-n-alpha-1">
            <td class="px-3 py-2 text-n-slate-12 truncate max-w-48">
              {{ row.name || '—' }}
            </td>
            <td class="px-3 py-2 tabular-nums text-n-slate-11">
              {{ row.phone_number }}
            </td>
            <td
              class="px-3 py-2 font-medium whitespace-nowrap"
              :class="RECIPIENT_STATUS_COLOR[row.status]"
            >
              {{ statusLabel(row) }}
            </td>
            <td class="px-3 py-2 whitespace-nowrap text-n-slate-11">
              {{ fmt(row.sent_at) }}
            </td>
            <td class="px-3 py-2 whitespace-nowrap text-n-slate-11">
              {{ fmt(row.delivered_at) }}
            </td>
            <td class="px-3 py-2 whitespace-nowrap text-n-slate-11">
              {{ fmt(row.read_at) }}
            </td>
            <td class="px-3 py-2 text-xs text-n-slate-11 max-w-64">
              <span
                v-if="row.error_code || row.error"
                class="block truncate"
                :title="row.error || ''"
                >{{ errorLabel(row) }}</span
              >
              <span
                v-if="row.next_retry_at && row.status === 'queued'"
                class="block text-n-amber-11"
              >
                {{
                  t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.NEXT_RETRY', {
                    date: fmt(row.next_retry_at),
                  })
                }}
              </span>
              <span v-if="row.attempts > 1" class="block text-n-slate-10">
                {{
                  t(
                    'WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.ATTEMPTS',
                    { count: row.attempts },
                    row.attempts
                  )
                }}
              </span>
            </td>
            <td class="px-3 py-2 text-right">
              <Button
                v-if="row.conversation_id"
                icon="i-lucide-message-square"
                variant="ghost"
                color="slate"
                size="xs"
                :title="
                  t('WASAPI_CAMPAIGNS.DETAIL.RECIPIENTS.OPEN_CONVERSATION')
                "
                @click="openConversation(row)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <PaginationFooter
      v-if="total > perPage"
      :current-page="page"
      :total-items="total"
      :items-per-page="perPage"
      @update:current-page="page = $event"
    />
  </div>
</template>
